import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Tiny header pill that shows the user's wallet balance. Click → /billing.
 * Re-fetches on mount and listens to `wallet:refresh` window events so other
 * components (e.g. after a successful purchase or chat consumption) can
 * trigger an update without prop drilling.
 */
export default function WalletBadge() {
  const [w, setW] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/me/wallet");
      setW(data);
    } catch {
      setW(null);
    }
  };

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("wallet:refresh", onRefresh);
    const t = setInterval(load, 30000); // gentle background refresh
    return () => {
      window.removeEventListener("wallet:refresh", onRefresh);
      clearInterval(t);
    };
  }, []);

  if (!w) return null;
  const low = w.is_low;

  return (
    <Link
      to="/billing"
      data-testid="wallet-badge"
      className={`group flex items-center gap-1.5 h-7 px-2.5 rounded-sm border text-xs font-mono transition ${
        low
          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20"
          : "border-border bg-muted/30 hover:bg-muted text-foreground"
      }`}
      title={low ? "Low balance — click to top up" : "Token wallet"}
    >
      <Zap className={`h-3 w-3 ${low ? "" : "text-primary"}`} />
      <span className="font-semibold tabular-nums">
        {Number(w.balance_tokens).toLocaleString()}
      </span>
      <span className="opacity-60 hidden sm:inline">tokens</span>
    </Link>
  );
}
